import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { STOCK_DECIMALS, formatQuantity, isQuantityDraft, parseQuantity } from '../utils/quantity';

type QuantityFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  /** The parent's parsed value; null/undefined shows an empty box. */
  value: number | null | undefined;
  /** The parsed value on every keystroke; null while the box holds no number yet ("" or "."). */
  onChange: (next: number | null) => void;
};

/**
 * A decimal quantity box (ADR-0038). A `type="number"` input bound to the parsed value can't hold an
 * in-between keystroke like "0." (React's controlled-input restore rewrites it), so this keeps the RAW
 * draft string as the visible value and hands the parent the parsed number (.claude/rules/frontend.md,
 * CommaListInput). A seeded value keeps all its stored places (9.125) so an untouched box round-trips.
 */
export default function QuantityField({
  value,
  onChange,
  onBlur,
  inputProps,
  ...rest
}: QuantityFieldProps) {
  const seeded = formatQuantity(value, STOCK_DECIMALS);
  const [draft, setDraft] = useState(seeded);

  useEffect(() => {
    // Re-seed only when the parent's value stops matching the draft (another row, a reset), never mid-keystroke.
    // An empty or "." draft matches 0 too: several parents store it as 0, and re-seeding "0" there turned ".53" into 53.
    if ((parseQuantity(draft, STOCK_DECIMALS) ?? 0) !== (value ?? 0)) setDraft(seeded);
    // `draft` is deliberately not a dependency, or the effect would fight every keystroke.
  }, [seeded]);

  return (
    <TextField
      {...rest}
      value={draft}
      inputProps={{ inputMode: 'decimal', ...inputProps }}
      onChange={(e) => {
        const text = e.target.value.replace(',', '.');
        // Refuse a keystroke that can't become a quantity (a letter, a third typed decimal).
        if (text !== draft && !isQuantityDraft(text)) return;
        setDraft(text);
        onChange(parseQuantity(text));
      }}
      onBlur={(e) => {
        const parsed = parseQuantity(draft, STOCK_DECIMALS);
        if (parsed !== null) setDraft(formatQuantity(parsed, STOCK_DECIMALS));
        onBlur?.(e);
      }}
    />
  );
}
